import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";
import apiServices from "../../service";

enableMapSet();


export interface StatementRecord {
  CommissionStatementId: string;
  ProviderId: string;
  ProviderName: string;
  StartDate: string;
  EndDate: string;
  ReceiptDate: string;
  DocumentId: string | null;
  FileName: string | null;
  ColumnMapping: ColumnMapping | null;
  Headers?: string[] | null;
  TotalRows?: number | null;
  ReceiptedRows?: number | null;
  Status: "IN_PROGRESS" | "COMPLETED";
  UpdatedAt: string | null;
  UpdatedBy: string | null;
  CreatedBy: string;
  CreatedAt: string;
}

export interface ColumnMapping {
  caseRef: string;
  actual: string;
  clientName?: string;
}

export interface BatchResult {
  appliedCount: number;
  totalAmount: number;
  partial?: boolean;
}

export interface ExpectationRecord {
  CommissionExpectationId: string;
  CommissionId: string;
  ExpectedAmount: string;
  DueDate: string;
  CommissionReceiptId: string | null;
  CaseRef: string;
  CommissionType: string | null;
  CommissionNote: string | null;
  ClientNameDb: string | null;
  JointIndicator: number | null;
}

export interface StatementDetail {
  CommissionStatementDetailId: string;
  RowNumber: number;
  CaseRef: string;
  ActualAmount: string;
  ClientNameFromFile: string;
  Processed: boolean;
}

interface CommissionStatementState {
  // Step 1 — provider + dates
  providerId: string;
  providerName: string;
  startDate: string;
  endDate: string;
  receiptDate: string;

  // Statement panel
  statements: StatementRecord[];
  allStatements: StatementRecord[];
  allStatementsTotal: number;
  selectedStatementId: string | null;
  statementsModalOpen: boolean;

  // Step 2 — file upload
  objectId: string | null;
  fileName: string | null;
  parsedHeaders: string[];
  parsedRows: any[];
  statementId: string | null;
  statementUpdatedAt: string | null;

  // Step 3 — column mapping
  columnMapping: ColumnMapping;
  suggestedMapping: Partial<ColumnMapping>;

  // Step 4 — v14 dual-panel
  detailRows: StatementDetail[];
  detailTotal: number;
  detailHasMore: boolean;
  loadingMoreDetail: boolean;
  expectations: ExpectationRecord[];
  expectationTotal: number;
  expectationHasMore: boolean;
  loadingMoreExpectations: boolean;
  selectedDetailIds: string[];
  selectedExpectationIds: Set<string>;
  actualAmounts: Record<string, string>;

  // Step 3 → PATCH mapping
  loadingMapping: boolean;
  errorMapping: string | null;
  mappingConfirmed: boolean;

  // Step 4 → GET detail
  loadingDetail: boolean;

  // Rollback snapshot for optimistic markStatementCompleted
  _markCompleteRollback: {
    statements: StatementRecord | null;
    allStatements: StatementRecord | null;
    selectedStatementId: string | null;
  } | null;

  // Step 5 — apply
  batchResult: BatchResult | null;
  applyConfirmOpen: boolean;

  // Current wizard step
  currentStep: number;

  // UI state
  loading: {
    statements: boolean;
    allStatements: boolean;
    parse: boolean;
    markComplete: boolean;
    apply: boolean;
    expectations: boolean;
  };
  error: {
    statements: string | null;
    parse: string | null;
    apply: string | null;
    allStatements: string | null;
    detail: string | null;
    expectations: string | null;
    markComplete: string | null;
  };
}

const initialColumnMapping: ColumnMapping = { caseRef: "", actual: "", clientName: "" };

const initialState: CommissionStatementState = {
  providerId: "",
  providerName: "",
  startDate: "",
  endDate: "",
  receiptDate: "",

  statements: [],
  allStatements: [],
  allStatementsTotal: 0,
  selectedStatementId: null,
  statementsModalOpen: false,

  objectId: null,
  fileName: null,
  parsedHeaders: [],
  parsedRows: [],
  statementId: null,
  statementUpdatedAt: null,

  columnMapping: { ...initialColumnMapping },
  suggestedMapping: {},

  detailRows: [],
  detailTotal: 0,
  detailHasMore: false,
  loadingMoreDetail: false,
  expectations: [],
  expectationTotal: 0,
  expectationHasMore: false,
  loadingMoreExpectations: false,
  selectedDetailIds: [],
  selectedExpectationIds: new Set<string>(),
  actualAmounts: {},

  loadingMapping: false,
  errorMapping: null,
  mappingConfirmed: false,
  loadingDetail: false,

  batchResult: null,
  applyConfirmOpen: false,

  currentStep: 0,

  loading: {
    statements: false,
    allStatements: false,
    parse: false,
    markComplete: false,
    apply: false,
    expectations: false,
  },
  error: {
    statements: null,
    parse: null,
    apply: null,
    allStatements: null,
    detail: null,
    expectations: null,
    markComplete: null,
  },
  _markCompleteRollback: null,
};

// ─── Async Thunks ────────────────────────────────────────────────────────────

const API_ERROR_CODE = 40000;
const isApiError = (body: { code?: number; isError?: boolean } | null | undefined): boolean =>
  !!body && (body.isError === true || (body.code != null && body.code >= API_ERROR_CODE));

export const fetchStatements = createAsyncThunk<
  StatementRecord[],
  { providerId: string; startDate: string; endDate: string },
  { rejectValue: string }
>(
  "commissionStatement/fetchStatements",
  async ({ providerId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.getStatements(providerId, startDate, endDate);
      return res.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to load statements");
    }
  }
);

export const fetchAllStatements = createAsyncThunk<
  { data: StatementRecord[]; total: number; limit: number; offset: number },
  { limit?: number; offset?: number; status?: string },
  { rejectValue: string }
>(
  "commissionStatement/fetchAllStatements",
  async ({ limit = 10, offset = 0, status } = {}, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.getAllStatements(limit, offset, status);
      const payload = res.data?.data;
      if (Array.isArray(payload)) {
        return { data: payload as StatementRecord[], total: payload.length, limit, offset };
      }
      return payload as { data: StatementRecord[]; total: number; limit: number; offset: number };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to load all statements");
    }
  }
);

export const parseCommissionFile = createAsyncThunk<
  { statementId: string; headers: string[] | null; rows: any[]; columnMapping: ColumnMapping | null },
  { objectId: string; providerId: string; providerName: string; startDate: string; endDate: string; receiptDate: string; fileName: string },
  { rejectValue: string }
>(
  "commissionStatement/parseFile",
  async (payload, { rejectWithValue }) => {
    try {
      const { providerName: _pn, ...apiPayload } = payload;
      const res = await apiServices.commissionStatement.uploadCommissionStatement(apiPayload);
      const body = res.data;
      if (isApiError(body)) {
        return rejectWithValue(
          body.message ||
          (body.code != null ? `Upload failed (code ${body.code})` : "The file could not be processed. It may be corrupted or unreadable. Please try a different file.")
        );
      }
      return body?.data || body;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to parse file");
    }
  }
);

export const markStatementCompleted = createAsyncThunk<
  string,
  { statementId: string },
  { rejectValue: string }
>(
  "commissionStatement/markStatementCompleted",
  async ({ statementId }, { rejectWithValue }) => {
    try {
      await apiServices.commissionStatement.updateCommissionStatement(statementId, { Status: "COMPLETED" });
      return statementId;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to mark statement as completed");
    }
  }
);

export const applyCommissionBatchV14 = createAsyncThunk<
  BatchResult,
  { statementId: string; receiptDate: string; processedStatementRows: string[]; selectedExpectations: { expectationId: string; actualAmount: number }[] },
  { rejectValue: string }
>(
  "commissionStatement/applyBatchV14",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.batchReceiptsV14(payload);
      const body = res.data;
      if (isApiError(body)) {
        return rejectWithValue(body.message || `Apply failed (code ${body.code})`);
      }
      return body?.data || body;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to apply receipts");
    }
  }
);

export const updateStatementMapping = createAsyncThunk<
  { data?: { ColumnMapping?: ColumnMapping | null } },
  { statementId: string; columnMapping: ColumnMapping },
  { rejectValue: string }
>(
  "commissionStatement/updateStatementMapping",
  async ({ statementId, columnMapping }, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.updateCommissionStatement(statementId, { ColumnMapping: columnMapping });
      if (isApiError(res.data)) {
        return rejectWithValue(res.data.message || "Failed to update column mapping");
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to update column mapping");
    }
  }
);

export const fetchStatementDetail = createAsyncThunk<
  { rows: StatementDetail[]; total: number; hasMore: boolean },
  string,
  { rejectValue: string }
>(
  "commissionStatement/fetchStatementDetail",
  async (statementId, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.getStatementDetail(statementId, { page: 1, limit: 50 });
      if (isApiError(res.data)) return rejectWithValue(res.data.message || "Failed to load detail");
      return res.data?.data as { rows: StatementDetail[]; total: number; hasMore: boolean };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to load detail");
    }
  },
  {
    condition: (_statementId, { getState }) => {
      const { loadingDetail } = (getState() as { commissionStatement: CommissionStatementState }).commissionStatement;
      return !loadingDetail;
    },
  }
);

export const fetchMoreDetailRows = createAsyncThunk<
  { rows: StatementDetail[]; total: number; hasMore: boolean },
  { statementId: string; page: number },
  { rejectValue: string }
>(
  "commissionStatement/fetchMoreDetailRows",
  async ({ statementId, page }, { rejectWithValue }) => {
    try {
      const res = await apiServices.commissionStatement.getStatementDetail(statementId, { page, limit: 50 });
      if (isApiError(res.data)) return rejectWithValue(res.data.message || "Failed to load detail page");
      return res.data?.data as { rows: StatementDetail[]; total: number; hasMore: boolean };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to load detail page");
    }
  },
  {
    condition: (_arg, { getState }) => {
      const { loadingMoreDetail } = (getState() as { commissionStatement: CommissionStatementState }).commissionStatement;
      return !loadingMoreDetail;
    },
  }
);

export const fetchExpectationsPage = createAsyncThunk<
  { data: ExpectationRecord[]; total: number; page: number; hasMore: boolean },
  { userId: string; startDate: string; endDate: string; providerId: string; page: number },
  { rejectValue: string }
>(
  "commissionStatement/fetchExpectationsPage",
  async ({ userId, startDate, endDate, providerId, page }, { rejectWithValue }) => {
    try {
      const dateCondition = `custom&start_date=${startDate}&end_date=${endDate}`;
      const res = await apiServices.commissionModule.receipting.getList(userId, dateCondition, page, 50, providerId);
      const raw: any[] = res.data?.data || [];
      // Map snake_case API fields to ExpectationRecord PascalCase shape
      const data: ExpectationRecord[] = raw.map((row) => ({
        CommissionExpectationId: row.expectation_id,
        CommissionId: row.commission_id,
        ExpectedAmount: String(row.expected ?? ""),
        DueDate: row.due_date ?? null,
        CommissionReceiptId: null,
        CaseRef: row.reference ?? "",
        CommissionType: row.commission_type ?? null,
        CommissionNote: row.commission_note ?? null,
        ClientNameDb: row.client_name ?? null,
        JointIndicator: null,
      }));
      const total = res.data?.count || 0;
      const totalPages = res.data?.pagination?.total_pages || Math.ceil(total / 50) || 1;
      return { data, total, page, hasMore: page < totalPages };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Failed to load expectations");
    }
  },
  {
    condition: ({ page }, { getState }) => {
      const { loading, loadingMoreExpectations } = (getState() as { commissionStatement: CommissionStatementState }).commissionStatement;
      return page === 1 ? !loading.expectations : !loadingMoreExpectations;
    },
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

export const commissionStatementSlice = createSlice({
  name: "commissionStatement",
  initialState,
  reducers: {
    setProviderDates: (
      state,
      action: PayloadAction<{
        providerId: string;
        providerName: string;
        startDate: string;
        endDate: string;
        receiptDate: string;
      }>
    ) => {
      const { providerId, providerName, startDate, endDate, receiptDate } = action.payload;
      state.providerId = providerId;
      state.providerName = providerName;
      state.startDate = startDate;
      state.endDate = endDate;
      state.receiptDate = receiptDate;
    },
    setSelectedStatement: (state, action: PayloadAction<string | null>) => {
      state.selectedStatementId = action.payload;
    },
    setStatementsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.statementsModalOpen = action.payload;
    },
    setResumedStatementMeta: (state, action: PayloadAction<{
      providerId: string;
      providerName: string;
      startDate: string;
      endDate: string;
      receiptDate: string;
      fileName: string | null;
      statementId: string;
      columnMapping: ColumnMapping | null;
    }>) => {
      const { providerId, providerName, startDate, endDate, receiptDate, fileName, statementId, columnMapping } = action.payload;
      state.providerId = providerId;
      state.providerName = providerName;
      state.startDate = startDate;
      state.endDate = endDate;
      state.receiptDate = receiptDate;
      state.fileName = fileName;
      state.statementId = statementId;
      state.detailRows = [];
      state.detailTotal = 0;
      state.detailHasMore = false;
      state.selectedDetailIds = [];
      state.selectedExpectationIds = new Set<string>();
      state.actualAmounts = {};
      state.expectations = [];
      state.mappingConfirmed = false;
      if (columnMapping) {
        state.columnMapping = columnMapping;
        state.parsedHeaders = Object.values(columnMapping).filter((v): v is string => typeof v === 'string' && v.trim() !== '');
        state.mappingConfirmed = true;
      }
    },
    setParsedHeaders: (state, action: PayloadAction<string[]>) => {
      state.parsedHeaders = action.payload;
    },
    setObjectId: (state, action: PayloadAction<string | null>) => {
      state.objectId = action.payload;
    },
    setFileName: (state, action: PayloadAction<string | null>) => {
      state.fileName = action.payload;
    },
    setColumnMapping: (state, action: PayloadAction<Partial<ColumnMapping>>) => {
      state.columnMapping = { ...state.columnMapping, ...action.payload };
    },
    setApplyConfirmOpen: (state, action: PayloadAction<boolean>) => {
      state.applyConfirmOpen = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
      state.loading.apply = false;
    },
    clearParseError: (state) => {
      state.error.parse = null;
    },
    clearBatchResult: (state) => {
      state.batchResult = null;
    },
    resetWizard: () => initialState,
    toggleExpectationSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const next = new Set(state.selectedExpectationIds);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      state.selectedExpectationIds = next;
    },
    setActualAmount: (state, action: PayloadAction<{ id: string; amount: string }>) => {
      state.actualAmounts = { ...state.actualAmounts, [action.payload.id]: action.payload.amount };
    },
    setSelectedExpectationIds: (state, action: PayloadAction<string[]>) => {
      state.selectedExpectationIds = new Set(action.payload);
    },
    toggleDetailId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.selectedDetailIds.indexOf(id);
      if (idx >= 0) {
        state.selectedDetailIds = state.selectedDetailIds.filter((x) => x !== id);
      } else {
        state.selectedDetailIds = [...state.selectedDetailIds, id];
      }
    },
    setSelectedDetailIds: (state, action: PayloadAction<string[]>) => {
      state.selectedDetailIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchStatements
      .addCase(fetchStatements.pending, (state) => {
        state.loading.statements = true;
        state.error.statements = null;
      })
      .addCase(fetchStatements.fulfilled, (state, action) => {
        state.statements = action.payload;
        state.loading.statements = false;
      })
      .addCase(fetchStatements.rejected, (state, action) => {
        state.loading.statements = false;
        state.error.statements = action.payload ?? null;
      })
      // fetchAllStatements
      .addCase(fetchAllStatements.pending, (state) => {
        state.loading.allStatements = true;
        state.error.allStatements = null;
      })
      .addCase(fetchAllStatements.fulfilled, (state, action) => {
        state.allStatements = action.payload?.data || [];
        state.allStatementsTotal = action.payload?.total ?? state.allStatements.length;
        state.loading.allStatements = false;
      })
      .addCase(fetchAllStatements.rejected, (state, action) => {
        state.loading.allStatements = false;
        state.error.allStatements = action.payload ?? null;
      })
      // parseCommissionFile
      .addCase(parseCommissionFile.pending, (state) => {
        state.loading.parse = true;
        state.error.parse = null;
      })
      .addCase(parseCommissionFile.fulfilled, (state, action) => {
        state.loading.parse = false;
        const { statementId, headers, rows, columnMapping } = action.payload;
        state.statementId = statementId;
        state.parsedHeaders = headers || [];
        state.parsedRows = rows || [];
        state.suggestedMapping = columnMapping || {};
        state.columnMapping = {
          caseRef: columnMapping?.caseRef || "",
          actual: columnMapping?.actual || "",
          clientName: columnMapping?.clientName || "",
        };
        state.statementUpdatedAt = null;
        state.batchResult = null;
        state.selectedStatementId = null;
        state.mappingConfirmed = false;

        const { providerId, providerName, startDate, endDate, receiptDate, fileName } = action.meta.arg;
        const alreadyTracked = state.statements.some(
          (s) => s.CommissionStatementId === statementId
        );
        if (!alreadyTracked) {
          state.statements.push({
            CommissionStatementId: statementId,
            ProviderId: providerId,
            ProviderName: providerName,
            StartDate: startDate,
            EndDate: endDate,
            ReceiptDate: receiptDate,
            DocumentId: null,
            FileName: fileName,
            ColumnMapping: columnMapping || null,
            Status: "IN_PROGRESS",
            UpdatedAt: null,
            UpdatedBy: null,
            CreatedBy: "",
            CreatedAt: new Date().toISOString(),
          });
        }
      })
      .addCase(parseCommissionFile.rejected, (state, action) => {
        state.loading.parse = false;
        state.error.parse = action.payload ?? null;
      })
      // markStatementCompleted
      .addCase(markStatementCompleted.pending, (state, action) => {
        state.loading.markComplete = true;
        state.error.markComplete = null;
        const id = action.meta.arg.statementId;
        state._markCompleteRollback = {
          statements: state.statements.find((s) => s.CommissionStatementId === id) ?? null,
          allStatements: state.allStatements.find((s) => s.CommissionStatementId === id) ?? null,
          selectedStatementId: state.selectedStatementId === id ? id : null,
        };
        state.statements = state.statements.filter((s) => s.CommissionStatementId !== id);
        state.allStatements = state.allStatements.filter((s) => s.CommissionStatementId !== id);
        if (state.selectedStatementId === id) state.selectedStatementId = null;
      })
      .addCase(markStatementCompleted.fulfilled, (state, action) => {
        state.loading.markComplete = false;
        state._markCompleteRollback = null;
        state.statements = state.statements.filter(
          (s) => s.CommissionStatementId !== action.payload
        );
        state.allStatements = state.allStatements.filter(
          (s) => s.CommissionStatementId !== action.payload
        );
        if (state.selectedStatementId === action.payload) {
          state.selectedStatementId = null;
        }
      })
      .addCase(markStatementCompleted.rejected, (state, action) => {
        state.loading.markComplete = false;
        state.error.markComplete = action.payload ?? null;
        if (state._markCompleteRollback) {
          const rb = state._markCompleteRollback;
          if (rb.statements) state.statements.push(rb.statements);
          if (rb.allStatements) state.allStatements.push(rb.allStatements);
          if (rb.selectedStatementId) state.selectedStatementId = rb.selectedStatementId;
          state._markCompleteRollback = null;
        }
      })
      // applyCommissionBatchV14
      .addCase(applyCommissionBatchV14.pending, (state) => {
        state.loading.apply = true;
        state.error.apply = null;
      })
      .addCase(applyCommissionBatchV14.fulfilled, (state, action) => {
        state.loading.apply = false;
        state.applyConfirmOpen = false;

        const requested = action.meta.arg.selectedExpectations.length;
        const confirmed = action.payload?.appliedCount ?? requested;
        state.batchResult = {
          ...action.payload,
          partial: confirmed < requested,
        };

        const appliedIds = new Set(
          action.meta.arg.selectedExpectations.map((e) => e.expectationId)
        );

        state.expectations = state.expectations.filter(
          (e) => !appliedIds.has(e.CommissionExpectationId)
        );
        state.selectedDetailIds = [];
        state.selectedExpectationIds = new Set<string>();
        const newAmounts = { ...state.actualAmounts };
        appliedIds.forEach((id) => delete newAmounts[id]);
        state.actualAmounts = newAmounts;
      })
      .addCase(applyCommissionBatchV14.rejected, (state, action) => {
        state.loading.apply = false;
        state.error.apply = action.payload ?? null;
      })
      // updateStatementMapping
      .addCase(updateStatementMapping.pending, (state) => {
        state.loadingMapping = true;
        state.errorMapping = null;
      })
      .addCase(updateStatementMapping.fulfilled, (state, action) => {
        state.loadingMapping = false;
        state.mappingConfirmed = true;
        const updatedMapping = action.payload?.data?.ColumnMapping;
        if (updatedMapping) state.columnMapping = updatedMapping;
      })
      .addCase(updateStatementMapping.rejected, (state, action) => {
        state.loadingMapping = false;
        state.errorMapping = action.payload ?? null;
      })
      // fetchStatementDetail
      .addCase(fetchStatementDetail.pending, (state) => {
        state.loadingDetail = true;
        state.error.detail = null;
      })
      .addCase(fetchStatementDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.statementId = action.meta.arg;
        state.detailRows = action.payload?.rows || [];
        state.detailTotal = action.payload?.total || 0;
        state.detailHasMore = action.payload?.hasMore || false;
        state.selectedDetailIds = [];
        state.selectedExpectationIds = new Set<string>();
        state.actualAmounts = {};
        state.mappingConfirmed = true;
        state.currentStep = 3;
      })
      .addCase(fetchStatementDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.error.detail = action.payload ?? null;
      })
      // fetchMoreDetailRows
      .addCase(fetchMoreDetailRows.pending, (state) => {
        state.loadingMoreDetail = true;
        state.error.detail = null;
      })
      .addCase(fetchMoreDetailRows.fulfilled, (state, action) => {
        state.loadingMoreDetail = false;
        state.detailRows = [...state.detailRows, ...(action.payload?.rows || [])];
        state.detailTotal = action.payload?.total || state.detailTotal;
        state.detailHasMore = action.payload?.hasMore || false;
      })
      .addCase(fetchMoreDetailRows.rejected, (state, action) => {
        state.loadingMoreDetail = false;
        state.error.detail = action.payload ?? null;
      })
      // fetchExpectationsPage
      .addCase(fetchExpectationsPage.pending, (state, action) => {
        if (action.meta.arg.page === 1) state.loading.expectations = true;
        else state.loadingMoreExpectations = true;
      })
      .addCase(fetchExpectationsPage.fulfilled, (state, action) => {
        state.loading.expectations = false;
        state.loadingMoreExpectations = false;
        if (action.payload.page === 1) {
          state.expectations = action.payload.data;
        } else {
          state.expectations = [...state.expectations, ...action.payload.data];
        }
        state.expectationHasMore = action.payload.hasMore;
        state.expectationTotal = action.payload.total;
      })
      .addCase(fetchExpectationsPage.rejected, (state, action) => {
        state.loading.expectations = false;
        state.loadingMoreExpectations = false;
        state.error.expectations = action.payload ?? null;
      });
  },
});

export const {
  setProviderDates,
  setSelectedStatement,
  setStatementsModalOpen,
  setResumedStatementMeta,
  setParsedHeaders,
  setObjectId,
  setFileName,
  setColumnMapping,
  setApplyConfirmOpen,
  setCurrentStep,
  clearParseError,
  clearBatchResult,
  resetWizard,
  toggleExpectationSelection,
  setActualAmount,
  setSelectedExpectationIds,
  toggleDetailId,
  setSelectedDetailIds,
} = commissionStatementSlice.actions;

export default commissionStatementSlice.reducer;
